import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { map, skipWhile, switchMap, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Store } from '@ngxs/store';
import { IMap } from '@nimbus-library/features/shared/models/interfaces';
import { IPageMetadata } from '@nimbus-library/features/menu';
import { ITitleProviderService } from '@nimbus-library/features/menu/services/contracts/i-title-provider.service';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { getIcuParameters } from '@nimbus-library/features/localization/utils/utils';
import { PluralForm } from '@nimbus-library/features/localization/models/enums/pluralform.enum';
import { LocalizationState } from '@nimbus-library/features/localization/store/localization.state';
import { Language } from '@nimbus-library/features/localization/models/enums/language.enum';

@Injectable()
export class TitleProviderService extends ITitleProviderService {
  private readonly prefix: string = 'Nimbus';

  protected titles: IMap<IPageMetadata> = {
    '/live-view': { title$: () => this.getTranslatedTitle(TranslationKeys.MenuLiveView) },
    '/dashboards': { title$: () => this.getTranslatedTitle(TranslationKeys.MenuDashboards) },
    '/statistics': { title$: () => this.getTranslatedTitle(TranslationKeys.MenuStatistics) },
    '/settings': { title$: () => this.getTranslatedTitle(TranslationKeys.MenuSettings) },
    '/sessions-list': { title$: () => this.getTranslatedTitle(TranslationKeys.MenuSessionsList) },
    '/workflow': { title$: () => this.getTranslatedTitle(TranslationKeys.MenuWorkflow) },
    '/authentication/login': { title$: () => this.getTranslatedTitle(null, 'Login') },
    '/personal/overview': { title$: () => this.getTranslatedTitle(TranslationKeys.MenuPersonalOverview) },
    '/personal/services-overview': { title$: () => this.getTranslatedTitle(TranslationKeys.MenuPersonalServicesOverview) },
    '/personal/my-sessions': {
      title$: (callerName: string) => this.getTranslatedTitle(TranslationKeys.MenuPersonalMySessions, callerName),
    },
    '/personal/attendantconsole': { title$: () => this.getTranslatedTitle(TranslationKeys.MenuPersonalAttendantconsole) },
    '/user-preferences': { title$: this.getTranslatedTitle(TranslationKeys.UserPreferencesTitle) },
    '/manage/workflow/resources': { title$: this.getTranslatedTitle(TranslationKeys.ManageWorkflowResources) },
    '/manage/workflow/playlist': { title$: this.getTranslatedTitle(TranslationKeys.ManageWorkflowPlaylistTitle) },
    '/manage/workflow/instances': { title$: this.getTranslatedTitle(TranslationKeys.ManageWorkflowInstancesTitle) },
    '/manage/codes/primary': { title$: this.getTranslatedTitle(TranslationKeys.GeneralPrimaryCode, undefined, true) },
    '/manage/codes/secondary': { title$: this.getTranslatedTitle(TranslationKeys.GeneralSecondaryCode, undefined, true) },
    '/manage/services/conversation-context': { title$: this.getTranslatedTitle(TranslationKeys.ManageServicesConversationContext) },
    '/manage/services/conversation-context-parameter': {
      title$: this.getTranslatedTitle(TranslationKeys.ManageServicesConversationContextParameter),
    },
    '/manage/services/opening-hours': { title$: this.getTranslatedTitle(TranslationKeys.ManageServicesOpeningHours) },
    '/settings/general': { title$: this.getTranslatedTitle(TranslationKeys.SettingsTabsGeneral) },
    '/settings/modalities': { title$: () => this.getTranslatedTitle(TranslationKeys.SettingsTabsModalities) },
    '/settings/distribution': { title$: () => this.getTranslatedTitle(TranslationKeys.SettingsTabsDistribution) },
    '/settings/context': { title$: () => this.getTranslatedTitle(TranslationKeys.SettingsTabsContext) },
    '/settings/extensions': { title$: () => this.getTranslatedTitle(TranslationKeys.SettingsTabsExtensions) },
    '/settings/permission': { title$: () => this.getTranslatedTitle(TranslationKeys.SettingsTabsPermission) },
    '/settings/agents': { title$: () => this.getTranslatedTitle(TranslationKeys.SettingsTabsAgents) },
    '/settings/interact': { title$: () => this.getTranslatedTitle(TranslationKeys.SettingsTabsInteract) },
    '*': { title$: this.getTranslatedTitle() },
  };

  public constructor(
    private readonly translateService: TranslateService,
    private readonly store: Store,
  ) {
    super();
  }

  private getTranslatedTitle(translationKey?: TranslationKeys, customParameter?: string, isPlural: boolean = false): Observable<string> {
    return translationKey
      ? this.store.select(LocalizationState.selectedLanguage).pipe(
          switchMap(() =>
            this.translateService.get(translationKey, isPlural ? getIcuParameters(translationKey, PluralForm.Other) : undefined),
          ),
          skipWhile(
            (translation) =>
              translation === translationKey && this.store.selectSnapshot(LocalizationState.selectedLanguage) !== Language.Debug,
          ),
          take(1),
          map((translation) => `${this.prefix} - ${customParameter || translation}`),
        )
      : of(customParameter ? `${this.prefix} - ${customParameter}` : this.prefix);
  }
}
